import { useEffect, useState } from "react";
import { api } from "../services/api";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import {
  clampDiscountRate,
  resolveItemDisplayVariantName,
  resolveItemUnitPrice,
  resolveProductImageUrl,
} from "../utils/commerce";
import "./Cart.css";

const getCartItemKey = (item) =>
  `${item.product._id}-${item.variantName || "default"}`;

const getCartItemPricing = (item, unitPrice) => {
  const discountRate = clampDiscountRate(item.product.discountRate);
  const originalPrice = unitPrice * item.quantity;
  const discountedPrice = originalPrice * (1 - discountRate / 100);

  return {
    hasDiscount: discountRate > 0,
    discountRate,
    originalPrice,
    discountedPrice,
    savedAmount: originalPrice - discountedPrice,
  };
};

export default function Cart() {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Cart | Balitah";
  }, []);

  const fetchCart = async () => {
    try {
      const data = await api.getCart();
      setCart(data.data.items || []);
    } catch (err) {
      console.error("Failed to fetch cart:", err);
      alert("Failed to fetch cart");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const updateQuantity = async (item, quantity) => {
    const productId = item.product._id;
    const variantName = item.variantName || "";

    try {
      if (quantity <= 0) {
        await api.removeFromCart(productId, variantName);
      } else {
        await api.updateCartQuantity(productId, quantity, variantName);
      }
      await fetchCart();
    } catch (error) {
      alert(error.message || "Failed to update quantity");
    }
  };

  const toggleSelected = async (item, selected) => {
    try {
      await api.toggleCartItemSelection(
        item.product._id,
        selected,
        item.variantName || "",
      );
      await fetchCart();
    } catch (error) {
      alert(error.message || "Failed to update selection");
    }
  };

  const toggleAllSelected = async (selectAll) => {
    try {
      if (cart.length === 0) {
        alert("No items in cart");
        return;
      }

      for (const item of cart) {
        await api.toggleCartItemSelection(
          item.product._id,
          selectAll,
          item.variantName || "",
        );
      }

      await fetchCart();
    } catch (error) {
      console.error("Error in toggleAllSelected:", error);
      alert(error.message || "Failed to update selection");
    }
  };

  const selectedCart = cart.filter((item) => item.selected !== false);
  const total = selectedCart.reduce((sum, item) => {
    return sum + resolveItemUnitPrice(item) * item.quantity;
  }, 0);
  const discount = selectedCart.reduce((sum, item) => {
    const unitPrice = resolveItemUnitPrice(item);
    const unitDiscount =
      unitPrice * (clampDiscountRate(item.product.discountRate) / 100);
    return sum + unitDiscount * item.quantity;
  }, 0);
  const finalTotal = total - discount;
  const allSelected =
    cart.length > 0 && cart.every((item) => item.selected !== false);
  const hasSelectedItems = selectedCart.length > 0;

  return (
    <div className="cart-page">
      <Navbar />

      <div className="cart-container">
        {/* Page Header */}
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <p>
            {cart.length} item{cart.length !== 1 ? "s" : ""} in your cart
          </p>
        </div>

        {loading ? (
          <div className="cart-loading">
            <div className="cart-spinner"></div>
            <p>Loading your cart...</p>
          </div>
        ) : cart.length === 0 ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything yet. Let's fix that!</p>
            <button
              className="cart-btn cart-btn-primary"
              onClick={() => navigate("/products")}
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="cart-content">
            {/* Cart Items */}
            <div className="cart-items-section">
              {/* Bulk Actions */}
              <div className="cart-bulk-actions">
                <label className="cart-select-all">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleAllSelected(e.target.checked)}
                  />
                  <span className="cart-checkbox-custom"></span>
                  <span>Select All ({cart.length})</span>
                </label>
                {hasSelectedItems && (
                  <span className="cart-selected-count">
                    {selectedCart.length} selected
                  </span>
                )}
              </div>

              {/* Items List */}
              <div className="cart-items-list">
                {cart.map((item) => {
                  const unitPrice = resolveItemUnitPrice(item);
                  const variantName = resolveItemDisplayVariantName(item);
                  const imageUrl = resolveProductImageUrl(item.product);
                  const {
                    hasDiscount,
                    discountRate,
                    originalPrice,
                    discountedPrice,
                    savedAmount,
                  } = getCartItemPricing(item, unitPrice);

                  return (
                    <div
                      key={getCartItemKey(item)}
                      className={`cart-item ${item.selected !== false ? "selected" : ""}`}
                    >
                      {/* Checkbox */}
                      <label className="cart-item-checkbox">
                        <input
                          type="checkbox"
                          checked={item.selected !== false}
                          onChange={(e) =>
                            toggleSelected(item, e.target.checked)
                          }
                        />
                        <span className="cart-checkbox-custom"></span>
                      </label>

                      {/* Product Image */}
                      <div className="cart-item-image">
                        {imageUrl ? (
                          <img src={imageUrl} alt={item.product.name} />
                        ) : (
                          <div className="cart-no-image">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            >
                              <rect
                                x="3"
                                y="3"
                                width="18"
                                height="18"
                                rx="2"
                                ry="2"
                              />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                        )}
                        {hasDiscount && (
                          <span className="cart-discount-badge">
                            -{discountRate}%
                          </span>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="cart-item-info">
                        <h3 className="cart-item-name">{item.product.name}</h3>
                        {variantName && (
                          <p className="cart-item-type">{variantName}</p>
                        )}

                        {/* Price Display */}
                        <div className="cart-item-pricing">
                          {hasDiscount ? (
                            <>
                              <span className="cart-price-original">
                                BND {originalPrice.toFixed(2)}
                              </span>
                              <span className="cart-price-final">
                                BND {discountedPrice.toFixed(2)}
                              </span>
                              <span className="cart-price-saved">
                                Save BND {savedAmount.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="cart-price-final">
                              BND {originalPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Quantity Controls */}
                      <div className="cart-item-quantity">
                        <button
                          className="cart-qty-btn"
                          onClick={() =>
                            updateQuantity(item, item.quantity - 1)
                          }
                          aria-label="Decrease quantity"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                        <span className="cart-qty-value">{item.quantity}</span>
                        <button
                          className="cart-qty-btn"
                          onClick={() =>
                            updateQuantity(item, item.quantity + 1)
                          }
                          aria-label="Increase quantity"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        className="cart-item-remove"
                        onClick={() => updateQuantity(item, 0)}
                        aria-label="Remove item"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Summary */}
            <div className="cart-summary">
              <div className="cart-summary-card">
                <h2>Order Summary</h2>

                <div className="cart-summary-rows">
                  <div className="cart-summary-row">
                    <span>
                      Subtotal ({selectedCart.length} item
                      {selectedCart.length !== 1 ? "s" : ""})
                    </span>
                    <span>BND {total.toFixed(2)}</span>
                  </div>

                  {discount > 0 && (
                    <div className="cart-summary-row cart-discount-row">
                      <span>Discount</span>
                      <span>-BND {discount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="cart-summary-row">
                    <span>Shipping</span>
                    <span className="cart-free">Free</span>
                  </div>
                </div>

                <div className="cart-summary-total">
                  <span>Total</span>
                  <span>BND {finalTotal.toFixed(2)}</span>
                </div>

                <button
                  className="cart-btn cart-btn-checkout"
                  disabled={!hasSelectedItems}
                  onClick={() => {
                    if (!hasSelectedItems) {
                      alert("Please select at least one item before checkout");
                      return;
                    }
                    navigate("/checkout");
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  Proceed to Checkout
                </button>

                {!hasSelectedItems && (
                  <p className="cart-select-hint">Select items to checkout</p>
                )}

                <button
                  className="cart-btn cart-btn-continue"
                  onClick={() => navigate("/products")}
                >
                  Continue Shopping
                </button>
              </div>

              {/* Security Badge */}
              <div className="cart-security">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Secure checkout with SSL encryption</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
